<template>
  <AppModal :show="isLoginModalOpen" @close="onClose()">
    <div class="modal-content">
      <template v-if="viewState === ViewState.Registration">
        <RegisterForm
          :saving="isBusy"
          :errors="validationErrors"
          @submit="onRegistrationSubmit($event)"
          @socialLoginSuccess="onSocialLoginSuccess"
        />

        <i18n-t
          keypath="user.switchToLogin"
          tag="div"
          class="modal-content__footer"
        >
          <template #link>
            <ButtonLink @click="switchToLogin()">
              {{ $t("user.switchToLoginLink") }}
            </ButtonLink>
          </template>
        </i18n-t>
      </template>

      <template v-else>
        <LoginForm
          v-if="viewState === ViewState.Login"
          :saving="isBusy"
          :errors="validationErrors"
          @submit="onLoginSubmit($event)"
          @socialLoginSuccess="onSocialLoginSuccess"
        >
          <ButtonLink class="mt-2" @click="switchToPasswordReminder()">
            {{ $t("user.forgotPassword") }}
          </ButtonLink>
        </LoginForm>

        <RecoverPasswordForm
          v-else-if="viewState === ViewState.RecoverPassword"
          :saving="isBusy"
          :errors="validationErrors"
          @submit="onRecoverSubmit($event)"
        >
          <ButtonLink class="mt-2" @click="switchToLogin()">
            {{ $t("user.backToLogin") }}
          </ButtonLink>
        </RecoverPasswordForm>

        <RemindPasswordForm
          v-else
          :saving="isBusy"
          :errors="validationErrors"
          @submit="onRemindSubmit($event)"
        >
          <ButtonLink class="mt-2" @click="switchToLogin()">
            {{ $t("user.backToLogin") }}
          </ButtonLink>
        </RemindPasswordForm>

        <i18n-t
          keypath="user.switchToRegister"
          tag="div"
          class="modal-content__footer"
        >
          <template #link>
            <ButtonLink @click="switchToRegistration()">
              {{ $t("user.switchToRegisterLink") }}
            </ButtonLink>
          </template>
        </i18n-t>
      </template>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import AppModal from "@/components/app-modal.vue";
import LoginForm from "./login-form.vue";
import RegisterForm from "./register-form.vue";
import RemindPasswordForm from "./remind-password-form.vue";
import RecoverPasswordForm from "./recover-password-form.vue";
import ButtonLink from "@/components/button-link.vue";
import { onMounted, ref } from "vue";
import {
  isBusy,
  isLoginModalOpen,
  openLoginModal,
  login,
  register,
  remind,
  isLoggedIn,
  recoverPassword,
  loginSocial,
} from "./store";
import {
  UserLoginData,
  UserRecoverPasswordData,
  UserRegisterData,
  UserRemindData,
} from "@/types";
import { getApiValidationErrors } from "@/utils/get-api-validation-errors";
import { getRecoverToken } from "./get-recover-token";
import { redirect } from "../../utils/redirect";

enum ViewState {
  Login,
  Registration,
  PasswordReminder,
  RecoverPassword,
}

const viewState = ref<ViewState>(ViewState.Login);
const validationErrors = ref<any>(null);

const emit = defineEmits<{
  (e: "socialLoginSuccess", payload: { action: "login" | "register" }): void;
}>();

const onClose = () => {
  openLoginModal(false);
  resetState();
};

const switchToRegistration = () => {
  resetState();
  viewState.value = ViewState.Registration;
};

const switchToLogin = () => {
  resetState();
  viewState.value = ViewState.Login;
};

const switchToPasswordReminder = () => {
  resetState();
  viewState.value = ViewState.PasswordReminder;
};

const onRegistrationSubmit = async (data: UserRegisterData): Promise<void> => {
  try {
    await register(data);
    resetState();
  } catch (error: unknown) {
    validationErrors.value = getApiValidationErrors(error);
  }
};

const onLoginSubmit = async (data: UserLoginData): Promise<void> => {
  try {
    await login(data);
    resetState();
  } catch (error: unknown) {
    validationErrors.value = getApiValidationErrors(error);
  }
};

const onSocialLoginSuccess = async (payload: {
  action: "login" | "register";
}) => {
  try {
    await loginSocial();
    resetState();
    emit("socialLoginSuccess", payload);
  } catch (error: unknown) {
    validationErrors.value = getApiValidationErrors(error);
  }
};

const onRemindSubmit = async (data: UserRemindData): Promise<void> => {
  try {
    await remind(data);
    resetState();
  } catch (error: unknown) {
    validationErrors.value = getApiValidationErrors(error);
  }
};

const onRecoverSubmit = async (
  data: UserRecoverPasswordData
): Promise<void> => {
  try {
    await recoverPassword(data);
    resetState();
  } catch (error: unknown) {
    validationErrors.value = getApiValidationErrors(error);
  }
};

const resetState = () => {
  viewState.value = ViewState.Login;
  validationErrors.value = null;
};

onMounted(async () => {
  if (isLoggedIn.value) {
    return;
  }

  // open modal if recover password url
  const isRecoverUrl = !!getRecoverToken();
  if (isRecoverUrl) {
    viewState.value = ViewState.RecoverPassword;
    openLoginModal();
    return;
  }

  // open modal if ?login=true and redirect to returnUrl if provided
  const params = new URLSearchParams(location.search);
  if (params.get("login") === "true") {
    const isLogged = await openLoginModal();
    if (isLogged && params.get("returnUrl")) {
      redirect(params.get("returnUrl")!);
    }
  }
});
</script>
